<template>
  <div class="calm-avatar" :class="['calm-avatar--' + size, { 'calm-avatar--editable': editable }]">
    <div
      class="calm-avatar__frame"
      :role="editable ? 'button' : null"
      :tabindex="editable ? 0 : null"
      @click="editable && openPicker()"
      @keydown.enter.prevent="editable && openPicker()"
      @keydown.space.prevent="editable && openPicker()"
    >
      <!-- image -->
      <img
        v-if="value"
        :src="value"
        class="calm-avatar__img"
        alt="avatar"
      />
      <!-- initials placeholder -->
      <span v-else-if="initials" class="calm-avatar__initials">{{ initials }}</span>
      <!-- silhouette placeholder -->
      <span v-else class="calm-avatar__silhouette"><i class="fas fa-user"></i></span>

      <!-- hover/tap overlay -->
      <div v-if="editable" class="calm-avatar__overlay">
        <button type="button" class="calm-avatar__action" @click.stop="openPicker()">
          <i class="fas fa-camera"></i>
          <span>เปลี่ยนรูป</span>
        </button>
        <button
          v-if="value"
          type="button"
          class="calm-avatar__action calm-avatar__action--danger"
          @click.stop="onRemove()"
        >
          <i class="fas fa-trash-alt"></i>
          <span>ลบ</span>
        </button>
      </div>
    </div>

    <input
      ref="file"
      type="file"
      class="calm-avatar__input"
      accept="image/jpeg,image/png"
      @change="onSelect"
    />

    <p v-if="error" class="calm-avatar__error">{{ error }}</p>
  </div>
</template>

<script>
const MAX_BYTES = 2 * 1024 * 1024; // 2MB
const ALLOWED = ["image/jpeg", "image/png"];

export default {
  name: "CalmAvatarUpload",
  props: {
    value: {
      type: String,
      default: ""
    },
    size: {
      type: String,
      default: "lg" // sm | md | lg
    },
    editable: {
      type: Boolean,
      default: true
    },
    name: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      error: ""
    };
  },
  computed: {
    initials() {
      const n = (this.name || "").trim();
      if (!n) return "";
      const parts = n.split(/\s+/).filter(Boolean);
      const a = parts[0] ? parts[0].charAt(0) : "";
      const b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
      return (a + b).toUpperCase();
    }
  },
  methods: {
    openPicker() {
      this.error = "";
      if (this.$refs.file) {
        this.$refs.file.value = "";
        this.$refs.file.click();
      }
    },
    onSelect(e) {
      const files = e && e.target ? e.target.files : null;
      if (!files || !files.length) return;
      const file = files[0];

      if (ALLOWED.indexOf(file.type) === -1) {
        this.error = "รองรับเฉพาะไฟล์ภาพ JPG หรือ PNG เท่านั้น";
        return;
      }
      if (file.size > MAX_BYTES) {
        this.error = "ขนาดไฟล์ต้องไม่เกิน 2 MB";
        return;
      }

      this.error = "";
      const reader = new FileReader();
      reader.onload = ev => {
        const dataUri = ev && ev.target ? ev.target.result : "";
        this.$emit("input", dataUri);
        this.$emit("change", file);
      };
      reader.onerror = () => {
        this.error = "ไม่สามารถอ่านไฟล์ภาพได้";
      };
      reader.readAsDataURL(file);
    },
    onRemove() {
      this.error = "";
      this.$emit("input", "");
      this.$emit("change", null);
      this.$emit("remove");
    }
  }
};
</script>

<style lang="scss" scoped>
/* CalmAvatarUpload — square rounded avatar with hover/tap upload overlay.
   Self-contained; relies only on Calm tokens (--c-*) defined on .calm-layout. */
.calm-avatar {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  font-family: var(--c-font);
}

.calm-avatar__frame {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  overflow: hidden;
  background: var(--c-blue-soft);
  border: 1px solid var(--c-border);
  color: var(--c-blue-deep);
  outline: none;
}
.calm-avatar--editable .calm-avatar__frame {
  cursor: pointer;
}
.calm-avatar__frame:focus-visible {
  box-shadow: 0 0 0 3px var(--c-blue-soft);
  border-color: var(--c-blue);
}

/* sizes */
.calm-avatar--sm .calm-avatar__frame {
  width: 40px;
  height: 40px;
  border-radius: 10px;
}
.calm-avatar--md .calm-avatar__frame {
  width: 96px;
  height: 96px;
  border-radius: 14px;
}
.calm-avatar--lg .calm-avatar__frame {
  width: 140px;
  height: 140px;
  border-radius: 16px;
}

.calm-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.calm-avatar__initials {
  font-weight: 600;
  color: var(--c-blue-deep);
}
.calm-avatar--sm .calm-avatar__initials {
  font-size: 14px;
}
.calm-avatar--md .calm-avatar__initials {
  font-size: 30px;
}
.calm-avatar--lg .calm-avatar__initials {
  font-size: 44px;
}

.calm-avatar__silhouette {
  color: var(--c-text-mute);
}
.calm-avatar--sm .calm-avatar__silhouette {
  font-size: 18px;
}
.calm-avatar--md .calm-avatar__silhouette {
  font-size: 40px;
}
.calm-avatar--lg .calm-avatar__silhouette {
  font-size: 60px;
}

/* overlay — visible on hover (desktop) and on focus-within (tap focus) */
.calm-avatar__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: rgba(44, 62, 80, 0.55);
  opacity: 0;
  transition: opacity var(--c-fast, 140ms) var(--c-ease, ease);
  padding: 6px;
}
.calm-avatar--editable .calm-avatar__frame:hover .calm-avatar__overlay,
.calm-avatar--editable .calm-avatar__frame:focus-within .calm-avatar__overlay {
  opacity: 1;
}

/* On small sizes (sm) the overlay text would not fit; hide labels there. */
.calm-avatar--sm .calm-avatar__action span {
  display: none;
}

.calm-avatar__action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  border-radius: 7px;
  padding: 4px 9px;
  background: var(--c-surface, #fff);
  color: var(--c-text, #2c3e50);
  font-family: var(--c-font);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  line-height: 1;
}
.calm-avatar__action i {
  font-size: 12px;
}
.calm-avatar__action:hover {
  background: var(--c-bg, #f4f6fa);
}
.calm-avatar__action--danger {
  color: var(--c-danger, #c0573e);
}

.calm-avatar__input {
  display: none;
}

.calm-avatar__error {
  margin: 8px 0 0;
  max-width: 180px;
  text-align: center;
  font-size: 12px;
  color: var(--c-danger, #c0573e);
}

/* Touch devices: no hover — keep overlay always faintly visible so the
   change/remove affordance is reachable by tap. */
@media (hover: none) {
  .calm-avatar--editable .calm-avatar__overlay {
    opacity: 1;
    background: rgba(44, 62, 80, 0.35);
  }
}
</style>
